import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import {
  getIndustrialCostCurrentCostResults,
  getIndustrialCostProducts,
} from '../services/industrialCostService';
import IndustrialCostSectionPlaceholder from '../components/IndustrialCostSectionPlaceholder';
import type {
  CurrentCostResult,
  Product,
} from '../types/industrialCost';

import { esNumberFormat } from '../utils/numberFormat';
function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const maybeResponse = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeResponse.response?.data?.detail) {
      return maybeResponse.response.data.detail;
    }

    if (maybeResponse.message) {
      return maybeResponse.message;
    }
  }

  return 'Se produjo un error inesperado.';
}

function formatCurrency(value: number | null | undefined, maximumFractionDigits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }

  return esNumberFormat({
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits,
  }).format(value);
}

function formatNumber(value: number | null | undefined, maximumFractionDigits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }

  return esNumberFormat({
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(value);
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }

  return esNumberFormat({
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value * 100) + ' %';
}

const MONTH_OPTIONS = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

function formatMonthLabel(month: number | null | undefined): string {
  if (!month) {
    return 'Mes no informado';
  }

  return MONTH_OPTIONS.find((item) => item.value === month)?.label ?? `Mes ${month}`;
}

function formatPeriod(year: number, month: number | null): string {
  if (!month) {
    return String(year);
  }

  return `${formatMonthLabel(month)} ${year}`;
}

function getNumericValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

type NumericBreakdown = Record<string, number>;

type CostLayerKey = 'all' | 'materials' | 'labor' | 'indirect';

type ProductCostWarning = {
  type: string;
  severity: string;
  layer: string;
  title: string;
  message: string;
  context?: Record<string, unknown>;
};

type ResultPayloadView = {
  kgProduced: number | null;
  unitsProduced: number | null;
  directCostUnit: number | null;
  indirectCostUnit: number | null;
  productionUnitCost: number | null;
  costPerKg: number | null;
  costPerUnit: number | null;
  directMaterialCostTotal: number;
  laborCostTotal: number;
  directCostBreakdown: NumericBreakdown;
  laborCostBreakdown: NumericBreakdown;
  stageBreakdown: NumericBreakdown;
  laborModel: string | null;
  laborValuationMode: string | null;
  costStatus: string | null;
  costStatusLabel: string | null;
  costWarnings: ProductCostWarning[];
};

function getNumericBreakdown(value: unknown): NumericBreakdown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const entries = Object.entries(value as Record<string, unknown>);

  return entries.reduce<NumericBreakdown>((acc, [key, rawValue]) => {
    if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
      acc[key] = rawValue;
    }

    return acc;
  }, {});
}

type EnrichedCostResult = CurrentCostResult & {
  id: string;
  product_name: string;
  product_code: string | null;
  product_family: string | null;
  payload_view: ResultPayloadView;
  stage_names: string[];
  stages_count: number;
  dominant_layer: Exclude<CostLayerKey, 'all'> | null;
  dominant_layer_amount: number;
  dominant_layer_share: number | null;
  production_label: string;
  status_label: string;
  status_tone: 'success' | 'warning' | 'error' | 'default';
};

function getProductCostWarnings(value: unknown): ProductCostWarning[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((item): item is Record<string, unknown> => {
      return typeof item === 'object' && item !== null && !Array.isArray(item);
    })
    .map((item) => {
      const context =
        typeof item.context === 'object' && item.context !== null && !Array.isArray(item.context)
          ? (item.context as Record<string, unknown>)
          : {};

      return {
        type: typeof item.type === 'string' ? item.type : 'cost_warning',
        severity: typeof item.severity === 'string' ? item.severity : 'warning',
        layer: typeof item.layer === 'string' ? item.layer : 'general',
        title: typeof item.title === 'string' ? item.title : 'Aviso del coste',
        message:
          typeof item.message === 'string'
            ? item.message
            : 'Existe un aviso que afecta a la lectura del coste de este producto.',
        context,
      };
    });
}

function getResultPayloadView(item: CurrentCostResult): ResultPayloadView {
  const payload =
    typeof item.result_payload === 'object' && item.result_payload !== null
      ? (item.result_payload as Record<string, unknown>)
      : null;

  return {
    kgProduced: getNumericValue(payload?.kg_produced),
    unitsProduced: getNumericValue(payload?.units_produced),
    directCostUnit: getNumericValue(payload?.direct_cost_unit),
    indirectCostUnit: getNumericValue(payload?.indirect_cost_unit),
    productionUnitCost: getNumericValue(payload?.production_unit_cost),
    costPerKg: getNumericValue(payload?.cost_per_kg),
    costPerUnit: getNumericValue(payload?.cost_per_unit),
    directMaterialCostTotal: getNumericValue(payload?.direct_material_cost_total) ?? 0,
    laborCostTotal: getNumericValue(payload?.labor_cost_total) ?? 0,
    directCostBreakdown: getNumericBreakdown(payload?.direct_cost_breakdown),
    laborCostBreakdown: getNumericBreakdown(payload?.labor_cost_breakdown),
    stageBreakdown: getNumericBreakdown(payload?.stage_breakdown),
    laborModel: typeof payload?.labor_model === 'string' ? payload.labor_model : null,
    laborValuationMode:
      typeof payload?.labor_valuation_mode === 'string'
        ? payload.labor_valuation_mode
        : null,
    costStatus: typeof payload?.cost_status === 'string' ? payload.cost_status : null,
    costStatusLabel:
      typeof payload?.cost_status_label === 'string' ? payload.cost_status_label : null,
    costWarnings: getProductCostWarnings(payload?.cost_warnings),
  };
}

function getSortedBreakdownEntries(breakdown: NumericBreakdown): Array<[string, number]> {
  return Object.entries(breakdown).sort((a, b) => {
    if (b[1] !== a[1]) {
      return b[1] - a[1];
    }

    return a[0].localeCompare(b[0], 'es');
  });
}

function getBreakdownLabel(key: string): string {
  if (key === '__unclassified__') {
    return 'Sin clasificar';
  }

  return key
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .trim()
    .replace(/^./, (letter: string) => letter.toUpperCase());
}

function getLaborModelLabel(model: string | null): string {
  if (model === 'stage_based') {
    return 'Por etapa';
  }

  if (model === 'product_rate_based') {
    return 'Por producto';
  }

  if (model === 'mixed') {
    return 'Mixto';
  }

  return 'No informado';
}

function getLaborValuationModeLabel(mode: string | null): string {
  if (mode === 'monthly_total') {
    return 'Coste total mensual';
  }

  if (mode === 'hourly_rate') {
    return 'Coste hora';
  }

  return 'No informado';
}

function getWarningLayerLabel(layer: string): string {
  if (layer === 'materials') {
    return 'Materiales';
  }

  if (layer === 'labor') {
    return 'Mano de obra';
  }

  if (layer === 'indirect') {
    return 'Indirectos';
  }

  if (layer === 'production') {
    return 'Producción';
  }

  return 'General';
}

function getWarningSeverityLabel(severity: string): string {
  if (severity === 'critical') {
    return 'Crítico';
  }

  if (severity === 'warning') {
    return 'Aviso';
  }

  return 'Informativo';
}

function buildProductionLabel(payload: ResultPayloadView, unitCostBasis: string): string {
  const parts: string[] = [];

  if ((payload.unitsProduced ?? 0) > 0) {
    parts.push(`${formatNumber(payload.unitsProduced, 2)} uds`);
  }

  if ((payload.kgProduced ?? 0) > 0) {
    parts.push(`${formatNumber(payload.kgProduced, 2)} kg`);
  }

  if (parts.length === 0) {
    return `Sin base (${unitCostBasis || '—'})`;
  }

  return parts.join(' · ');
}

function resolveDominantLayer(item: CurrentCostResult, payloadView: ResultPayloadView): {
  dominantLayer: Exclude<CostLayerKey, 'all'> | null;
  dominantLayerAmount: number;
  dominantLayerShare: number | null;
} {
  const candidates: Array<{ key: Exclude<CostLayerKey, 'all'>; amount: number }> = [
    { key: 'materials', amount: payloadView.directMaterialCostTotal },
    { key: 'labor', amount: payloadView.laborCostTotal },
    { key: 'indirect', amount: item.indirect_cost_total },
  ];

  const dominant = candidates.sort((a, b) => b.amount - a.amount)[0];

  if (!dominant || dominant.amount <= 0 || item.total_cost <= 0) {
    return {
      dominantLayer: null,
      dominantLayerAmount: 0,
      dominantLayerShare: null,
    };
  }

  return {
    dominantLayer: dominant.key,
    dominantLayerAmount: dominant.amount,
    dominantLayerShare: dominant.amount / item.total_cost,
  };
}

type BreakdownBlockProps = {
  title: string;
  subtitle?: string;
  entries: Array<[string, number]>;
  total: number;
  emptyText: string;
};

function BreakdownBlock({ title, subtitle, entries, total, emptyText }: BreakdownBlockProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(148,163,184,0.14)',
        bgcolor: 'rgba(15,23,42,0.48)',
        p: 2,
      }}
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 900 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>

        {entries.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            {emptyText}
          </Typography>
        ) : (
          <Stack spacing={1.2}>
            {entries.map(([label, amount]) => {
              const share = total > 0 ? amount / total : 0;

              return (
                <Box key={label}>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 700 }}>
                      {getBreakdownLabel(label)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      {formatCurrency(amount)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.6 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(Math.max(share * 100, 0), 100)}
                      sx={{
                        flex: 1,
                        height: 7,
                        borderRadius: 999,
                        bgcolor: 'rgba(148,163,184,0.16)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 999,
                          bgcolor: 'rgba(56,189,248,0.78)',
                        },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#94a3b8', minWidth: 48, textAlign: 'right' }}>
                      {formatPercent(share)}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

export default function IndustrialCostResultsView() {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [yearFilter, setYearFilter] = useState<string>(String(currentYear));
  const [monthFilter, setMonthFilter] = useState<string>(String(currentMonth));
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);

  const effectivePeriodYear = Number(yearFilter);
  const effectivePeriodMonth = Number(monthFilter);

  const [resultsQuery, productsQuery] = useQueries({
    queries: [
      {
        queryKey: [
          'industrial-cost-current-cost-results',
          effectivePeriodYear,
          effectivePeriodMonth,
        ],
        queryFn: () =>
          getIndustrialCostCurrentCostResults(
            effectivePeriodYear,
            effectivePeriodMonth,
          ),
        enabled:
          Number.isFinite(effectivePeriodYear) &&
          Number.isFinite(effectivePeriodMonth) &&
          effectivePeriodMonth >= 1 &&
          effectivePeriodMonth <= 12,
      },
      {
        queryKey: ['industrial-cost-products'],
        queryFn: getIndustrialCostProducts,
      },
    ],
  });

  const results = resultsQuery.data ?? [];
  const products = productsQuery.data ?? [];

  const productsMap = useMemo(
    () => new Map<string, Product>(products.map((item) => [item.id, item])),
    [products],
  );

  const enrichedResults = useMemo<EnrichedCostResult[]>(() => {
    return results.map((item) => {
      const payloadView = getResultPayloadView(item);
      const product = productsMap.get(item.product_id);
      const productName = product?.name || product?.code || item.product_id;
      const stageNames = Object.keys(payloadView.stageBreakdown).sort((a, b) =>
        a.localeCompare(b, 'es'),
      );
      const dominant = resolveDominantLayer(item, payloadView);
      const hasProductionBase =
        (payloadView.unitsProduced ?? 0) > 0 || (payloadView.kgProduced ?? 0) > 0;
      const hasCost = item.total_cost > 0;
      const costStatus = payloadView.costStatus;
      const fallbackStatusLabel = hasCost && hasProductionBase
        ? 'Completo'
        : hasProductionBase
          ? 'Sin coste'
          : 'Sin producción';

      const statusTone: EnrichedCostResult['status_tone'] =
        costStatus === 'complete'
          ? 'success'
          : costStatus === 'partial' || costStatus === 'missing'
            ? 'error'
            : costStatus === 'warning'
              ? 'warning'
              : hasCost && hasProductionBase
                ? 'success'
                : hasProductionBase
                  ? 'warning'
                  : 'default';

      return {
        ...item,
        id: `${item.product_id}-${item.period_year}-${item.period_month ?? 'null'}`,
        product_name: productName,
        product_code: product?.code ?? null,
        product_family: product?.family ?? null,
        payload_view: payloadView,
        stage_names: stageNames,
        stages_count: stageNames.length,
        dominant_layer: dominant.dominantLayer,
        dominant_layer_amount: dominant.dominantLayerAmount,
        dominant_layer_share: dominant.dominantLayerShare,
        production_label: buildProductionLabel(payloadView, item.unit_cost_basis),
        status_label: payloadView.costStatusLabel ?? fallbackStatusLabel,
        status_tone: statusTone,
      };
    });
  }, [results, productsMap]);

  const availableYears = useMemo(() => {
    return Array.from({ length: 7 }, (_, index) => String(currentYear - 3 + index));
  }, [currentYear]);

  const filteredResults = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return enrichedResults
      .filter((item) => {
        const searchableText = [
          item.product_code,
          item.product_name,
          item.product_id,
          item.product_family,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return normalizedSearch.length === 0 || searchableText.includes(normalizedSearch);
      })
      .sort((a, b) => b.total_cost - a.total_cost);
  }, [enrichedResults, searchTerm]);

  const totals = useMemo(() => {
    const productsCount = filteredResults.length;
    const totalDirect = filteredResults.reduce((acc, item) => acc + item.direct_cost_total, 0);
    const totalMaterials = filteredResults.reduce(
      (acc, item) => acc + item.payload_view.directMaterialCostTotal,
      0,
    );
    const totalLabor = filteredResults.reduce(
      (acc, item) => acc + item.payload_view.laborCostTotal,
      0,
    );
    const totalIndirect = filteredResults.reduce((acc, item) => acc + item.indirect_cost_total, 0);
    const totalCost = filteredResults.reduce((acc, item) => acc + item.total_cost, 0);
    const totalUnits = filteredResults.reduce(
      (acc, item) => acc + (item.payload_view.unitsProduced ?? 0),
      0,
    );
    const totalKg = filteredResults.reduce(
      (acc, item) => acc + (item.payload_view.kgProduced ?? 0),
      0,
    );
    const calculatedProducts = filteredResults.filter((item) => item.total_cost > 0).length;
    const unitCostProducts = filteredResults.filter((item) => item.unit_cost_basis === 'units');
    const totalUnitBasisCost = unitCostProducts.reduce((acc, item) => acc + item.total_cost, 0);
    const totalUnitBasisUnits = unitCostProducts.reduce(
      (acc, item) => acc + (item.payload_view.unitsProduced ?? 0),
      0,
    );

    return {
      productsCount,
      calculatedProducts,
      totalDirect,
      totalMaterials,
      totalLabor,
      totalIndirect,
      totalCost,
      totalUnits,
      totalKg,
      directShare: totalCost > 0 ? totalDirect / totalCost : null,
      materialsShare: totalCost > 0 ? totalMaterials / totalCost : null,
      laborShare: totalCost > 0 ? totalLabor / totalCost : null,
      indirectShare: totalCost > 0 ? totalIndirect / totalCost : null,
      weightedUnitCost:
        totalUnitBasisUnits > 0 ? totalUnitBasisCost / totalUnitBasisUnits : null,
    };
  }, [filteredResults]);

  const selectedResult = useMemo(() => {
    return (
      filteredResults.find((item) => item.id === selectedResultId) ??
      filteredResults[0] ??
      null
    );
  }, [filteredResults, selectedResultId]);

  const selectedDirectMaterialBreakdown = useMemo(() => {
    if (!selectedResult) {
      return [];
    }

    return getSortedBreakdownEntries(selectedResult.payload_view.directCostBreakdown);
  }, [selectedResult]);

  const selectedLaborBreakdown = useMemo(() => {
    if (!selectedResult) {
      return [];
    }

    return getSortedBreakdownEntries(selectedResult.payload_view.laborCostBreakdown);
  }, [selectedResult]);

  const selectedStageBreakdown = useMemo(() => {
    if (!selectedResult) {
      return [];
    }

    return getSortedBreakdownEntries(selectedResult.payload_view.stageBreakdown);
  }, [selectedResult]);

  const selectedCostWarnings = useMemo(() => {
    return selectedResult?.payload_view.costWarnings ?? [];
  }, [selectedResult]);

  const selectedLayerRows = useMemo(() => {
    if (!selectedResult) {
      return [];
    }

    const directMaterial = selectedResult.payload_view.directMaterialCostTotal;
    const labor = selectedResult.payload_view.laborCostTotal;
    const indirect = selectedResult.indirect_cost_total;
    const total = selectedResult.total_cost;

    return [
      {
        key: 'materials',
        label: 'Material directo',
        amount: directMaterial,
        share: total > 0 ? directMaterial / total : null,
        helper: 'Derivado de reglas técnicas, valoración/PMP y directos aplicados.',
      },
      {
        key: 'labor',
        label: 'Mano de obra',
        amount: labor,
        share: total > 0 ? labor / total : null,
        helper: 'Calculada según la configuración laboral activa del periodo.',
      },
      {
        key: 'indirect',
        label: 'Coste indirecto',
        amount: indirect,
        share: total > 0 ? indirect / total : null,
        helper: 'Asignado mediante reparto primario, secundario y reglas finales.',
      },
    ];
  }, [selectedResult]);

  const isLoading = resultsQuery.isLoading || productsQuery.isLoading;
  const isError = resultsQuery.isError || productsQuery.isError;
  const error = resultsQuery.error || productsQuery.error;

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.72)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack spacing={1.15} sx={{ maxWidth: 820 }}>
            <Typography variant="overline" sx={{ color: '#38bdf8', fontWeight: 900, letterSpacing: 1.2 }}>
              PROA Costing · Resultados
            </Typography>
            <Typography variant="h4" sx={{ color: '#f8fafc', fontWeight: 950, lineHeight: 1.12 }}>
              Coste de producción por producto y mes
            </Typography>
            <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7 }}>
              Consulta viva del coste calculado para el periodo seleccionado. La pantalla separa
              material directo, mano de obra e indirectos para entender cuánto cuesta cada producto
              y qué capa explica ese coste.
            </Typography>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              minWidth: { xs: '100%', md: 340 },
              borderRadius: 3,
              border: '1px solid rgba(56,189,248,0.22)',
              bgcolor: 'rgba(8,47,73,0.32)',
              p: 2,
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" sx={{ color: '#7dd3fc', fontWeight: 800 }}>
                  Periodo activo
                </Typography>
                <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.5 }}>
                  {formatPeriod(effectivePeriodYear, effectivePeriodMonth)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#bae6fd', mt: 0.75 }}>
                  Sin snapshots visibles. Calculado contra el estado actual del sistema.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1.25,
                }}
              >
                <TextField
                  select
                  label="Año"
                  value={yearFilter}
                  onChange={(e) => {
                    setYearFilter(e.target.value);
                    setSelectedResultId(null);
                  }}
                  fullWidth
                  size="small"
                >
                  {availableYears.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Mes"
                  value={monthFilter}
                  onChange={(e) => {
                    setMonthFilter(e.target.value);
                    setSelectedResultId(null);
                  }}
                  fullWidth
                  size="small"
                >
                  {MONTH_OPTIONS.map((month) => (
                    <MenuItem key={month.value} value={String(month.value)}>
                      {month.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Stack>
          </Paper>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 1.5,
        }}
      >
        {[
          {
            label: 'Coste total visible',
            value: formatCurrency(totals.totalCost),
            helper: `${totals.productsCount} producto(s) filtrado(s)`,
          },
          {
            label: 'Coste directo total',
            value: formatCurrency(totals.totalDirect),
            helper: `Material ${formatCurrency(totals.totalMaterials)} · MO ${formatCurrency(totals.totalLabor)}`,
          },
          {
            label: 'Coste indirecto total',
            value: formatCurrency(totals.totalIndirect),
            helper: `${formatPercent(totals.indirectShare)} del coste visible`,
          },
          {
            label: 'Coste medio unitario',
            value: formatCurrency(totals.weightedUnitCost, 4),
            helper: totals.weightedUnitCost === null ? 'Sin base de unidades suficiente' : 'Ponderado sobre productos con unidades',
          },
        ].map((item) => (
          <Paper
            key={item.label}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(148,163,184,0.16)',
              bgcolor: 'rgba(15,23,42,0.58)',
              px: 2,
              py: 1.75,
            }}
          >
            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700 }}>
              {item.label}
            </Typography>
            <Typography
              variant="h5"
              sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.45 }}
            >
              {item.value}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.6 }}>
              {item.helper}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.55fr 1fr' },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                <Box>
                  <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                    Coste por producto
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                    Vista mensual por producto. Busca una referencia y selecciona una fila para abrir el desglose.
                  </Typography>
                </Box>
                <Chip
                  label={`${totals.calculatedProducts}/${totals.productsCount} con coste`}
                  size="small"
                  sx={{
                    alignSelf: { xs: 'flex-start', md: 'center' },
                    color: '#bae6fd',
                    bgcolor: 'rgba(14,116,144,0.22)',
                    border: '1px solid rgba(56,189,248,0.22)',
                    fontWeight: 800,
                  }}
                />
              </Stack>

              <TextField
                label="Buscar producto"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSelectedResultId(null);
                }}
                size="small"
                fullWidth
                placeholder="Referencia, nombre o identificador visible"
                sx={{ maxWidth: 420 }}
              />
            </Stack>
          </Box>

          {isError ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{getErrorMessage(error)}</Alert>
            </Box>
          ) : isLoading ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Calculando resultados actuales...
              </Typography>
            </Box>
          ) : results.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <IndustrialCostSectionPlaceholder
                title="Sin resultados calculables para el periodo"
                description="No hay producción con coste calculable para el mes seleccionado o faltan datos base para obtener resultados."
              />
            </Box>
          ) : filteredResults.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <IndustrialCostSectionPlaceholder
                title="Sin resultados para la búsqueda actual"
                description="Ajusta el buscador para volver a mostrar productos con coste calculado."
              />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{
                  width: '100%',
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': {
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 900, width: '30%' }}>
                      Producto
                    </TableCell>
                    <TableCell sx={{ color: '#64748b', fontWeight: 800, width: '25%' }}>
                      Producción
                    </TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 900, width: '15%' }} align="right">
                      Coste total
                    </TableCell>
                    <TableCell
                      sx={{
                        color: '#7dd3fc',
                        fontWeight: 950,
                        width: '18%',
                        whiteSpace: 'nowrap',
                      }}
                      align="right"
                    >
                      Coste unitario
                    </TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 900, width: '12%' }}>
                      Estado
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredResults.map((item) => {
                    const isSelected = selectedResult?.id === item.id;

                    return (
                      <TableRow
                        key={item.id}
                        hover
                        selected={isSelected}
                        onClick={() => setSelectedResultId(item.id)}
                        sx={{
                          cursor: 'pointer',
                          '& .MuiTableCell-root': {
                            borderColor: 'rgba(148,163,184,0.08)',
                            py: 1.25,
                          },
                          ...(isSelected
                            ? {
                                bgcolor: 'rgba(56,189,248,0.12)',
                              }
                            : null),
                        }}
                      >
                        <TableCell sx={{ minWidth: 120, width: 130 }}>
                          <Typography
                            variant="body2"
                            sx={{ color: '#f8fafc', fontWeight: 900, whiteSpace: 'nowrap' }}
                          >
                            {item.product_code || item.product_name || item.product_id}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#94a3b8',
                            width: '11%',
                            fontSize: '0.84rem',
                            fontWeight: 600,
                          }}
                        >
                          {item.production_label}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#f8fafc', fontWeight: 950 }}>
                          {formatCurrency(item.total_cost)}
                        </TableCell>
                        <TableCell align="right" sx={{ width: '18%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Box
                              sx={{
                                width: '100%',
                                maxWidth: 150,
                                borderRadius: 2,
                                border: '1px solid rgba(56,189,248,0.24)',
                                bgcolor: 'rgba(14,116,144,0.16)',
                                px: 1.1,
                                py: 0.75,
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={0.55}
                                alignItems="baseline"
                                justifyContent="flex-end"
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: '#f8fafc',
                                    fontWeight: 950,
                                    lineHeight: 1.1,
                                  }}
                                >
                                  {formatCurrency(item.unit_cost, 4)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#7dd3fc',
                                    fontWeight: 700,
                                    lineHeight: 1.1,
                                  }}
                                >
                                  /{item.unit_cost_basis === 'units' ? 'ud' : item.unit_cost_basis}
                                </Typography>
                              </Stack>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ width: 110 }}>
                          <Chip
                            label={item.status_label}
                            size="small"
                            color={
                              item.status_tone === 'success'
                                ? 'success'
                                : item.status_tone === 'warning'
                                  ? 'warning'
                                  : item.status_tone === 'error'
                                    ? 'error'
                                    : 'default'
                            }
                            variant="outlined"
                            sx={{ alignSelf: 'flex-start', fontWeight: 800 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>

        <Stack spacing={1.5}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148,163,184,0.18)',
              bgcolor: 'rgba(15,23,42,0.66)',
              backdropFilter: 'blur(10px)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
              <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                Desglose del producto
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                Lectura de composición para la fila seleccionada.
              </Typography>
            </Box>

            {!selectedResult ? (
              <Box sx={{ p: 3 }}>
                <IndustrialCostSectionPlaceholder
                  title="Selecciona un producto"
                  description="El desglose aparecerá aquí cuando haya un resultado seleccionado."
                />
              </Box>
            ) : (
              <Stack spacing={2.25} sx={{ p: 3 }}>
                <Box>
                  <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 950 }}>
                    {selectedResult.product_code
                      ? `${selectedResult.product_code} · ${selectedResult.product_name}`
                      : selectedResult.product_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                    {formatPeriod(selectedResult.period_year, selectedResult.period_month)} · {selectedResult.production_label}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(148,163,184,0.14)',
                      bgcolor: 'rgba(2,6,23,0.24)',
                      p: 1.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      Coste total
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.3 }}>
                      {formatCurrency(selectedResult.total_cost)}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(148,163,184,0.14)',
                      bgcolor: 'rgba(2,6,23,0.24)',
                      p: 1.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      Coste unitario
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 950, mt: 0.3 }}>
                      {formatCurrency(selectedResult.unit_cost, 4)}
                    </Typography>
                  </Paper>
                </Box>

                {selectedCostWarnings.length > 0 ? (
                  <>
                    <Divider sx={{ borderColor: 'rgba(148,163,184,0.14)' }} />

                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: '1px solid rgba(251,191,36,0.22)',
                        bgcolor: 'rgba(120,53,15,0.18)',
                        p: 2,
                      }}
                    >
                      <Stack spacing={1.4}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#fef3c7', fontWeight: 950 }}>
                            Avisos del coste
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#fde68a', mt: 0.4 }}>
                            Este producto tiene datos calculados, pero hay aspectos que afectan a la lectura del coste.
                          </Typography>
                        </Box>

                        <Stack spacing={1}>
                          {selectedCostWarnings.map((warning, index) => (
                            <Paper
                              key={`${warning.type}-${index}`}
                              elevation={0}
                              sx={{
                                borderRadius: 2,
                                border: warning.severity === 'critical'
                                  ? '1px solid rgba(248,113,113,0.28)'
                                  : '1px solid rgba(251,191,36,0.22)',
                                bgcolor: warning.severity === 'critical'
                                  ? 'rgba(127,29,29,0.18)'
                                  : 'rgba(15,23,42,0.34)',
                                p: 1.4,
                              }}
                            >
                              <Stack spacing={0.7}>
                                <Stack
                                  direction="row"
                                  spacing={0.8}
                                  alignItems="center"
                                  flexWrap="wrap"
                                  useFlexGap
                                >
                                  <Chip
                                    label={getWarningSeverityLabel(warning.severity)}
                                    size="small"
                                    color={warning.severity === 'critical' ? 'error' : 'warning'}
                                    variant="outlined"
                                    sx={{ fontWeight: 800 }}
                                  />
                                  <Chip
                                    label={getWarningLayerLabel(warning.layer)}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      color: '#cbd5e1',
                                      borderColor: 'rgba(148,163,184,0.26)',
                                      fontWeight: 800,
                                    }}
                                  />
                                </Stack>

                                <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                                  {warning.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#cbd5e1', lineHeight: 1.55 }}>
                                  {warning.message}
                                </Typography>
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      </Stack>
                    </Paper>
                  </>
                ) : null}

                <Divider sx={{ borderColor: 'rgba(148,163,184,0.14)' }} />

                <Stack spacing={1.4}>
                  {selectedLayerRows.map((row) => (
                    <Box key={row.key}>
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Box>
                          <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                            {row.label}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {row.helper}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" sx={{ minWidth: 116 }}>
                          <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 950 }}>
                            {formatCurrency(row.amount)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            {formatPercent(row.share)}
                          </Typography>
                        </Stack>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(Math.max((row.share ?? 0) * 100, 0), 100)}
                        sx={{
                          mt: 0.8,
                          height: 8,
                          borderRadius: 999,
                          bgcolor: 'rgba(148,163,184,0.16)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 999,
                            bgcolor: row.key === 'materials'
                              ? 'rgba(34,197,94,0.78)'
                              : row.key === 'labor'
                                ? 'rgba(251,191,36,0.78)'
                                : 'rgba(56,189,248,0.78)',
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Stack>

                <Divider sx={{ borderColor: 'rgba(148,163,184,0.14)' }} />

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={`MO: ${getLaborModelLabel(selectedResult.payload_view.laborModel)}`}
                    sx={{ color: '#fde68a', bgcolor: 'rgba(120,53,15,0.28)', fontWeight: 800 }}
                  />
                  <Chip
                    size="small"
                    label={getLaborValuationModeLabel(selectedResult.payload_view.laborValuationMode)}
                    sx={{ color: '#fde68a', bgcolor: 'rgba(120,53,15,0.28)', fontWeight: 800 }}
                  />
                  <Chip
                    size="small"
                    label={`${selectedResult.stages_count} etapa(s) indirectas`}
                    sx={{ color: '#bae6fd', bgcolor: 'rgba(14,116,144,0.24)', fontWeight: 800 }}
                  />
                </Stack>
              </Stack>
            )}
          </Paper>

          {selectedResult ? (
            <Stack spacing={1.5}>
              <BreakdownBlock
                title="Material directo"
                subtitle="Clasificaciones aplicadas al coste directo derivado o manual."
                entries={selectedDirectMaterialBreakdown}
                total={selectedResult.payload_view.directMaterialCostTotal}
                emptyText="No hay desglose de material directo para este producto."
              />

              <BreakdownBlock
                title="Mano de obra"
                subtitle="Coste laboral repartido o calculado para el producto."
                entries={selectedLaborBreakdown}
                total={selectedResult.payload_view.laborCostTotal}
                emptyText="No hay coste laboral desglosado para este producto."
              />

              <BreakdownBlock
                title="Indirectos por etapa"
                subtitle="Resultado del reparto primario, secundario y final."
                entries={selectedStageBreakdown}
                total={selectedResult.indirect_cost_total}
                emptyText="No hay indirectos imputados a este producto."
              />
            </Stack>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  );
}
